import { useEffect, useRef, useState } from "react";

import { Button, Card, Col, DatePicker, Form, Row, Space, Typography } from "antd";
import { getData } from "apiServices/common";
import moment from "moment";
import CumulativeProfitLossStatement from "./CumulativeProfitLossStatement";
import DailyProfitLossStatement from "./DailyProfitLossStatement";
import DateRangePresets from "common/SearchForm/DateRangePresets";

const PerformanceFolding = () => {
  const [todayData, setTodayData] = useState([]);
  const [upToDateData, setUpToDateData] = useState([]);
  const [loadingTodayData, setLoadingTodayData] = useState(false);
  const [loadingUpToDateData, setLoadingUpToDateData] = useState(false);
  // Fix round 1: needed to tell "never searched" apart from "searched, found
  // nothing" — the A3 empty-state hint must only show after an actual search
  // (the mount effect below now counts as one, via form.submit()).
  const [hasSearched, setHasSearched] = useState(false);
  // Final fix wave (I1): distinguish "the window is genuinely empty" from "the request
  // failed/timed out" for each of the two independent fetches below.
  const [todayError, setTodayError] = useState(false);
  const [upToDateError, setUpToDateError] = useState(false);
  const [form] = Form.useForm();
  const startDateWatch = Form.useWatch("start_date", form) || moment();
  const endDateWatch = Form.useWatch("end_date", form) || moment();

  // Fix round 1: guard against setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Round 2 final wave: request-sequence ref — now that auto-fetch runs on mount again,
  // a fast manual Filter must never be overwritten by the slower mount-time fetch
  // resolving afterwards (or vice versa).
  const reqIdRef = useRef(0);

  const getDataForReport = async (firstDate, lastDate) => {
    try {
      const query = `/api/pnl-report/daily-profit-loss-statement?start_date=${firstDate}&end_date=${lastDate}`;
      const res = await getData(query);

      // The API response structure is: res.data.data (array of profit/loss items)
      const responseData = res.data?.data;

      if (!Array.isArray(responseData)) {
        console.log("API response data is not an array:", responseData);
        // Final fix wave (I1): an unexpected shape is a failure, not "no rows" —
        // null is the failure sentinel so the caller can render an error state.
        return null;
      }

      return responseData.map((item, index) => ({
        ...item,
        key: index.toString(), // Add key for table rendering
      }));
    } catch (error) {
      console.log(error);
      // Final fix wave (I1): null is the failure sentinel — distinct from a genuinely
      // empty [] — so the caller can render an error state instead of "no records".
      return null;
    }
  };

  const onFilter = async (values) => {
    // in start_date and end_date are current month
    const startDateOfCurrentMonth = moment().startOf("month");
    const endDateOfCurrentMonth = moment();
    const { start_date, end_date } = values;
    const startDate = start_date
      ? moment(start_date).format("YYYY-MM-DD")
      : startDateOfCurrentMonth.format("YYYY-MM-DD");
    const endDate = end_date ? moment(end_date).format("YYYY-MM-DD") : endDateOfCurrentMonth.format("YYYY-MM-DD");

    // Set loading for both today's data and cumulative data
    setLoadingTodayData(true);
    setLoadingUpToDateData(true);
    setHasSearched(true);

    // Round 2 final wave: claim this request's sequence number before the awaits so a
    // later, faster request (the mount-time auto-fetch racing a quick manual Filter, or
    // the reverse) can invalidate this one.
    const myReq = ++reqIdRef.current;

    try {
      // Get today's daily profit loss data (using end date as today)
      const dailyProfitLossData = await getDataForReport(endDate, endDate);

      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      // Final fix wave (I1): null means the request failed/timed out.
      setTodayError(dailyProfitLossData === null);
      setTodayData(dailyProfitLossData || []);

      // Get cumulative profit loss data for the selected date range
      const cumulativeProfitLossData = await getDataForReport(startDate, endDate);

      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      // Final fix wave (I1): null means the request failed/timed out.
      setUpToDateError(cumulativeProfitLossData === null);
      setUpToDateData(cumulativeProfitLossData || []);
    } catch (error) {
      console.log(error);
    } finally {
      if (mountedRef.current && myReq === reqIdRef.current) {
        setLoadingTodayData(false);
        setLoadingUpToDateData(false);
      }
    }
  };

  useEffect(() => {
    // Round 2 final wave: restore the mount-time fetch. Round 1 removed it because a
    // direct timing check measured this endpoint at ~60.8s with a 500, over the
    // backend's 60s execution limit; the set-based rewrite (REVIEW-C2 §5) now answers
    // the seeded current-month default in ~1-4s even under load, a 5x+ margin to the
    // 60s fatal, so auto-fetching this specific pre-seeded window is safe again. Seed
    // the form with the same current-month default onFilter's own fallback (L43-44)
    // computes, then submit through the same onFinish={onFilter} path the Filter button
    // uses — no duplicate fetch logic. The mounted-ref and request-sequence guards above
    // cover a manual Filter/Reset racing this fetch.
    const defaultStartDate = moment().startOf("month");
    const defaultEndDate = moment();
    form.setFieldsValue({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
    });
    form.submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      title={`Profit & Loss Statement | ${
        startDateWatch && endDateWatch
          ? `(${moment(startDateWatch).format("DD-MMM-YY")} to ${moment(endDateWatch).format("DD-MMM-YY")})`
          : ""
      }`}
    >
      <Row>
        <Col span={24}>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFilter}
            onReset={() => {
              form.setFieldsValue({
                start_date: moment(),
                end_date: moment(),
              });
            }}
            initialValues={{
              start_date: moment(),
              end_date: moment(),
            }}
          >
            <Row gutter={24} align={"middle"}>
              <Col span={24}>
                <DateRangePresets form={form} startName="start_date" endName="end_date" />
              </Col>
              <Col span={3}>
                <Form.Item
                  label="Start Date"
                  name="start_date"
                  rules={[
                    {
                      required: true,
                      message: "Please select start date",
                    },
                    {
                      validator: async (_, value) => {
                        if (value && value > endDateWatch) {
                          return Promise.reject(new Error("Start date must be less than today"));
                        }
                      },
                    },
                  ]}
                >
                  <DatePicker
                    allowClear={false}
                    disabledDate={(current) => {
                      if (!current) {
                        return false;
                      }

                      return current > moment();
                    }}
                    // showTime={{ format: "DD-MMM-YY" }}
                    format="DD-MMM-YY"
                    style={{ width: "100%" }}
                    defaultValue={moment()}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  label="End Date"
                  name="end_date"
                  rules={[
                    {
                      required: true,
                      message: "Please select start date",
                    },
                    {
                      validator: async (_, value) => {
                        if (value && value < startDateWatch) {
                          return Promise.reject(new Error("End date must be greater than start date"));
                        }
                      },
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    allowClear={false}
                    disabledDate={(current) => {
                      if (!current) {
                        return false;
                      }
                      const endOfMonth = moment(startDateWatch).endOf("month");
                      const startOfMonth = moment(startDateWatch).startOf("month");
                      return current >= endOfMonth || current <= startOfMonth;
                    }}
                    // showTime={{ format: "DD-MMM-YY" }}
                    format="DD-MMM-YY"
                    defaultValue={moment()}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="reset"
                    danger
                    style={{
                      marginTop: "10px",
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{
                      marginTop: "10px",
                    }}
                  >
                    Filter
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col span={24}>
          <DailyProfitLossStatement
            todayData={todayData}
            loadingTodayData={loadingTodayData}
            endDateWatch={endDateWatch}
          />
          {/* Final fix wave (I1): a failed/timed-out request must never be rendered as an
              empty-window hint. */}
          {hasSearched && !loadingTodayData && todayError && (
            <Typography.Text type="danger">
              The report could not be loaded (timed out). Try a shorter range or press Search again.
            </Typography.Text>
          )}
          {hasSearched && !loadingTodayData && !todayError && (todayData?.length || 0) === 0 && (
            <Typography.Text type="secondary">
              {`No records for ${moment(endDateWatch).format("DD-MMM-YYYY")}. Change the date above.`}
            </Typography.Text>
          )}
        </Col>
        <Col span={24}>
          <CumulativeProfitLossStatement
            upToDateData={upToDateData}
            loadingUpToDateData={loadingUpToDateData}
            startDateWatch={startDateWatch}
            endDateWatch={endDateWatch}
          />
          {/* Final fix wave (I1): a failed/timed-out request must never be rendered as an
              empty-window hint. */}
          {hasSearched && !loadingUpToDateData && upToDateError && (
            <Typography.Text type="danger">
              The report could not be loaded (timed out). Try a shorter range or press Search again.
            </Typography.Text>
          )}
          {hasSearched && !loadingUpToDateData && !upToDateError && (upToDateData?.length || 0) === 0 && (
            <Typography.Text type="secondary">
              {`No records between ${moment(startDateWatch).format("DD-MMM-YYYY")} and ${moment(
                endDateWatch,
              ).format("DD-MMM-YYYY")}. Widen the range above.`}
            </Typography.Text>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default PerformanceFolding;
